import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isUndefined } from 'lodash'
import { useMemo } from 'react'
import { ApiResult } from 'src/Libs/api'
import { addWallbox as addWallboxAPI } from 'src/Libs/wallboxes/api/wallboxes.api'
import { WallboxConfigurationModel } from '../models'
import { AddWallboxDto } from '../dto/add-wallbox.dto'
import { useParams } from 'react-router-dom'
import { useFarmContext } from 'src/Elements/Farm'
import { WallboxStandardConfigurationDto } from '../dto/wallbox-configuration-update.dto'
import useWallboxConfigurationMutation from './use-wallbox-configuration-mutation'
import useWallboxConfigurationQueries from './use-wallbox-configuration-queries'
import useWallboxHardwareByStorage from './use-wallbox-hardware-by-storage'

interface UseWallboxesHook {
  wallboxes: WallboxConfigurationModel[]
  updateSettings: (storageId: string, resourceId: string, model: WallboxStandardConfigurationDto, farmMasterStorageId?: string) => Promise<void>
  addWallbox: (storageId: string, model: AddWallboxDto) => Promise<ApiResult<number>>
  isError: boolean
  isLoading: boolean
}

/**
 * @returns wallboxes: all configuration wallboxes of a storage, including the offline ones
 * @returns updateSettings: function to update wallbox configuration settings
 * @returns addWallbox: function to add a wallbox to the system, returns the new wallbox index in case of success
 * @returns isError
 * @returns isLoading
 *
 */
const useWallboxes = (): UseWallboxesHook => {
  const { storageId } = useParams<{ storageId: string }>()
  const { farmMaster } = useFarmContext()
  const authFetch = useAuthFetchWithResponse()

  const {
    data: configurationWallboxes,
    isLoading: isWallboxConfigurationLoading,
    isError: isWallboxConfigurationError,
  } = useWallboxConfigurationQueries()
  const { mutateWallboxConfiguration } = useWallboxConfigurationMutation()
  const { data: hardwareWallboxes, isLoading: isWallboxHardwareLoading, isError: isWallboxHardwareError } = useWallboxHardwareByStorage()

  const updateSettings = async (wallboxStorageId: string, wallboxIndex: string, model: WallboxStandardConfigurationDto): Promise<void> => {
    if (isUndefined(wallboxStorageId) || isUndefined(wallboxIndex)) {
      throw new Error(`Invalid activeStorageId or tabIndex (id: ${wallboxStorageId}, tabIndex: ${wallboxIndex})`)
    }
    await mutateWallboxConfiguration(wallboxStorageId, wallboxIndex, model as Partial<WallboxConfigurationModel>)
  }

  const addWallbox = async (storageId: string, model: AddWallboxDto): Promise<ApiResult<number>> => {
    if (isUndefined(storageId)) {
      throw new Error(`Invalid storageId (id: ${storageId})`)
    }
    return await addWallboxAPI(storageId, model, authFetch)
  }

  // ? Combine configuration wallboxes with derived not available wallboxes to have a complete list of wallboxes for the storage, including the offline ones
  const wallboxes = useMemo(
    () =>
      hardwareWallboxes?.map(hardwareWallbox => {
        const foundConfigurationWallbox = configurationWallboxes?.find(
          cw =>
            cw.wallboxIndex === hardwareWallbox.number &&
            (cw.storageSerialNo === hardwareWallbox.storageSerialNumber.toString() || cw.storageSerialNo === farmMaster?.storageId)
        )

        return foundConfigurationWallbox
          ? {
              ...foundConfigurationWallbox,
              storageSerialNo: hardwareWallbox.storageSerialNumber.toString(),
              serverStorageSerialNo: farmMaster?.storageId || storageId,
              isAvailable: true,
            }
          : ({
              storageSerialNo: hardwareWallbox.storageSerialNumber.toString(),
              wallboxIndex: hardwareWallbox.number,
              deviceName: hardwareWallbox.deviceName,
              wallboxType: hardwareWallbox.wallboxType,
              serverStorageSerialNo: farmMaster?.storageId || storageId,
              isAvailable: false,
            } as WallboxConfigurationModel)
      }) || [],
    [hardwareWallboxes, configurationWallboxes, farmMaster?.storageId, storageId]
  )

  return {
    updateSettings,
    wallboxes,
    addWallbox,
    isError: isWallboxConfigurationError || isWallboxHardwareError,
    isLoading: isWallboxConfigurationLoading || isWallboxHardwareLoading,
  }
}

export default useWallboxes
